import { useId, useRef } from "react";

import type { ReviewProposal } from "@/api";
import { formatMm } from "@/lib/format";
import { dimensionLine, projectRun, type MmPoint } from "@/lib/projection";
import { useBoxSize } from "@/lib/useBoxSize";

import { stateWord, type ReviewRow } from "./model";
import styles from "./Review.module.css";

const PADDING = 48;
const LABEL_OFFSET = 16;
const MONO_CHAR_PX = 6.7;

interface ProposalPaneProps {
  /** The settled form, from the latest check — the reading's, or a dry run's with the person's figures. */
  proposal: ReviewProposal;
  rows: ReviewRow[];
  activeWallId: string | null;
  /** The walls the ring's hint names (§10.1) — marked, not judged. */
  hinted?: ReadonlySet<string>;
  onHover: (wallId: string | null) => void;
  onPick: (wallId: string) => void;
}

/** „Lücke: waagerecht 15.750 mm“ — what the gap is, for the drawing's title. */
function gapWords(gap: NonNullable<ReviewProposal["gap"]>): string {
  const parts = [
    gap.dxMm !== 0 ? `waagerecht ${formatMm(Math.abs(gap.dxMm))}` : null,
    gap.dyMm !== 0 ? `senkrecht ${formatMm(Math.abs(gap.dyMm))}` : null,
  ].filter(Boolean);
  return `Lücke: ${parts.join(", ")}`;
}

/**
 * The outline as the system proposes it, to scale (plan 4 §5) — **one
 * source**: the geometry `settle` drew (`review.proposal`, §9.1), on arrival
 * and after every figure a person sets, because the dry run answers with it.
 * So a figure that does not fit shows in the form itself (E-13: the geometry
 * is the error detector) — a ring that no longer meets keeps its gap open,
 * drawn as a dashed bridge between its two ends.
 *
 * Every wall carries its state in the *line*, not only in its colour: solid
 * for checked, dashed for open, dotted for no figure, solid on a band for a
 * person's figure. Checked and set walls print their figure; an open wall
 * carries its plaque „W3 ?“ — the one thing to look for. The walls the
 * ring's hint names lie on a highlighter's stroke.
 *
 * The active wall (hovered or focused anywhere on the screen) is drawn wider
 * with a halo. Pointer only: the list is the keyboard's way through the
 * walls, and fifteen more tab stops in a drawing would only be in its way.
 */
export function ProposalPane({ proposal, rows, activeWallId, hinted, onHover, onPick }: ProposalPaneProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const { width, height } = useBoxSize(boxRef, { width: 560, height: 420 });
  const byId = new Map(rows.map((row) => [row.wall.wallId, row]));

  const nodes: MmPoint[] = proposal.walls.flatMap((wall, index) => [
    { id: `s${index}`, xMm: wall.startMm[0], yMm: wall.startMm[1] },
    { id: `e${index}`, xMm: wall.endMm[0], yMm: wall.endMm[1] },
  ]);
  const projection = projectRun(nodes, { width, height, padding: PADDING });

  const walls = proposal.walls
    .map((wall, index) => ({
      wallId: wall.wallId,
      row: byId.get(wall.wallId) ?? null,
      from: projection.points[`s${index}`]!,
      to: projection.points[`e${index}`]!,
    }))
    .filter((wall) => wall.row !== null);
  const first = walls[0];
  const last = walls.at(-1);
  // A ring that does not meet itself shows its gap; a chain that was never a
  // ring shows its two open ends.
  const gap = proposal.gap !== null && !proposal.closed && first && last ? { from: last.to, to: first.from } : null;
  const openEnds = !proposal.closed && proposal.gap === null && first && last ? [first.from, last.to] : [];

  const count = (state: ReviewRow["state"]) => rows.filter((row) => row.state === state).length;
  const summary = [
    `${rows.length} Wände`,
    `${count("geprueft")} geprüft`,
    ...(count("gelesen") > 0 ? [`${count("gelesen")} übereinstimmend gelesen`] : []),
    `${count("offen") + count("uneinig")} offen`,
    `${count("von_dir")} von dir`,
    `${count("kein_mass")} ohne Maß`,
    ...(proposal.gap !== null && !proposal.closed ? [gapWords(proposal.gap)] : []),
  ].join(", ");

  return (
    // The proposal is drawn to scale — a sheet, paper in the dark too
    // (design 2026-10-24 §5.3), a drawing of ours on warm paper (B-20).
    <div ref={boxRef} className={styles.paneBox} data-blatt="zeichnung">
      <svg
        className={styles.proposal}
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        role="img"
        aria-labelledby={titleId}
        onPointerLeave={() => onHover(null)}
      >
        <title id={titleId}>{`Vorschlag, maßstäblich: ${summary}.`}</title>

        {hinted && hinted.size > 0
          ? walls.map(({ wallId, from, to }) =>
              hinted.has(wallId) ? (
                <line
                  key={`hint-${wallId}`}
                  className={styles.hintMark}
                  data-role="proposal-hint"
                  data-wall={wallId}
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                />
              ) : null,
            )
          : null}

        {walls.map(({ wallId, from, to }) =>
          wallId === activeWallId ? (
            <line key={`halo-${wallId}`} className={styles.halo} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
          ) : null,
        )}

        {walls.map(({ wallId, row, from, to }) => (
          <g key={wallId} data-wall={wallId} data-state={row!.state} data-active={wallId === activeWallId}>
            {row!.state === "von_dir" ? (
              <line className={styles.band} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
            ) : null}
            <line className={styles.wallLine} data-state={row!.state} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
          </g>
        ))}

        {gap ? (
          <g data-role="proposal-gap">
            <line className={styles.gapLine} x1={gap.from.x} y1={gap.from.y} x2={gap.to.x} y2={gap.to.y} />
            <circle className={styles.gapEnd} cx={gap.from.x} cy={gap.from.y} r={4.5} />
            <circle className={styles.gapEnd} cx={gap.to.x} cy={gap.to.y} r={4.5} />
          </g>
        ) : null}

        {openEnds.map((point, index) => (
          <circle key={index} className={styles.openEnd} cx={point.x} cy={point.y} r={4.5} />
        ))}

        {walls.map(({ wallId, row, from, to }) => {
          const mark = dimensionLine(from, to, projection.centroid, LABEL_OFFSET);
          const state = row!.state;
          if (state === "offen" || state === "uneinig") {
            const text = `${row!.wall.label} ?`;
            const w = text.length * MONO_CHAR_PX + 10;
            return (
              <g key={`plaque-${wallId}`} className={styles.svgPlaque} aria-hidden="true">
                <rect x={mark.labelX - w / 2} y={mark.labelY - 9} width={w} height={18} rx={2} />
                <text x={mark.labelX} y={mark.labelY} textAnchor="middle" dominantBaseline="central">
                  {text}
                </text>
              </g>
            );
          }
          if (row!.valueMm === null) return null;
          // A figure longer than its wall would sit on the neighbours' —
          // there it waits until the wall is the active one; the list says
          // it anyway.
          const text = formatMm(row!.valueMm);
          const room = Math.hypot(to.x - from.x, to.y - from.y);
          if (room < text.length * MONO_CHAR_PX && wallId !== activeWallId) return null;
          return (
            <text
              key={`figure-${wallId}`}
              className={styles.figure}
              data-state={state}
              x={mark.labelX}
              y={mark.labelY}
              textAnchor="middle"
              dominantBaseline="central"
              transform={mark.rotation ? `rotate(${mark.rotation} ${mark.labelX} ${mark.labelY})` : undefined}
              aria-hidden="true"
            >
              {text}
            </text>
          );
        })}

        {/* Last, so they lie over everything they stand for. */}
        {walls.map(({ wallId, row, from, to }) => (
          <line
            key={`hit-${wallId}`}
            className={styles.hit}
            data-role="proposal-wall"
            data-wall={wallId}
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
            onPointerEnter={() => onHover(wallId)}
            onClick={() => onPick(wallId)}
          >
            <title>{`${row!.wall.label} · ${stateWord(row!.state)}`}</title>
          </line>
        ))}
      </svg>
    </div>
  );
}

/**
 * The line styles in words, under the drawing — read once, then known. A
 * reading has either checked walls (its ring was checked) or read ones (it
 * was not), never both, so the legend names the one it shows. The gap is
 * named only where there is one.
 */
export function ProposalLegend({ ringChecked = true, gap = false }: { ringChecked?: boolean; gap?: boolean }) {
  const items: { state: ReviewRow["state"] | "gap"; word: string }[] = [
    ringChecked ? { state: "geprueft", word: "geprüft" } : { state: "gelesen", word: "gelesen" },
    { state: "offen", word: "offen" },
    { state: "kein_mass", word: "kein Maß" },
    { state: "von_dir", word: "von dir" },
    ...(gap ? [{ state: "gap" as const, word: "Lücke" }] : []),
  ];
  return (
    <ul className={styles.legend} aria-label="Linien">
      {items.map((item) => (
        <li key={item.state}>
          <svg width="22" height="8" aria-hidden="true">
            {item.state === "gap" ? (
              <line className={styles.gapLine} x1="2" y1="4" x2="20" y2="4" />
            ) : (
              <>
                {item.state === "von_dir" ? <line className={styles.band} x1="2" y1="4" x2="20" y2="4" /> : null}
                <line className={styles.wallLine} data-state={item.state} x1="2" y1="4" x2="20" y2="4" />
              </>
            )}
          </svg>
          {item.word}
        </li>
      ))}
    </ul>
  );
}
